// @ts-nocheck

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { FreebetsIcon } from '../../assets/img/betting';
import SoldeAsset from '../../assets/img/betting/Solde.png';
import { Target } from '../../client';
import {
  USER_INFO_AMOUNT_ICON_WIDTH,
  USER_INFO_AMOUNT_LEFT_MARGIN,
  USER_INFO_SIDE_MARGIN,
} from '../../constants/ui/userInfo';
import { usePrevious } from '../../hooks';
import Formatter from '../../i18n/formatter';
import { getCurrency } from '../../i18n/l10n';
import { type UserBankrollEntityVariationState } from '../../states/user';
import theme from '../../styled-components/dark';
import palette from '../../styled-components/palette';
import NumberUtils from '../../utils/number';
import AnimatedDigitsLabel from '../AnimatedDigitsLabel';

const DIFF_ANIM_DURATION = 1500;
const TEXT_DISPLAY_TRANSITION_DELAY = 400;

export const BANKROLL_CHANGES_START_ANIM_DELAY = 1800;
export const BANKROLL_CHANGES_ANIMATION_DURATION =
  DIFF_ANIM_DURATION + TEXT_DISPLAY_TRANSITION_DELAY + BANKROLL_CHANGES_START_ANIM_DELAY;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: stretch;
  max-height: 3em;
  flex-direction: column;

  margin: 0 ${USER_INFO_SIDE_MARGIN / 2}em;
  font-size: 1.4em;

  @media screen and (max-width: 300px) {
    font-size: 1.3em;
  }

  ${Target.__LIBRARY__
    ? css`
        font-size: 1.9em;
      `
    : undefined}

  @keyframes scale-up-15 {
    50% {
      transform: scale(1.15);
    }
  }
`;

const amountLineSharedStyle = css`
  position: relative;
  margin: 0;
  color: #fff;
  font-size: 0.7em;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RealValueAmoutLine = styled.span`
  ${amountLineSharedStyle}
`;

const FreeBetValueAmountLine = styled.span`
  ${amountLineSharedStyle}
`;

const sharedImagesStyle = css`
  height: 1.2em;
`;

const SoldeImg = styled.img`
  ${sharedImagesStyle}
`;

const FreeBetsImg = styled.img`
  ${sharedImagesStyle}
`;

const diffTranslationAnimation = keyframes`
  0% {
    transform: translate(-100%, 3.5em);
  }

  90% {
    transform: translate(-100%);
  }

  100% {
    transform: translate(-100%);
  }
`;

const diffOpacityAnimation = keyframes`
  0% {
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  90% {
    opacity: 1;
  }

  100% {
    opactiy: 0;
  }
`;

const sharedAnimatedDiffStyle = css`
  position: absolute;
  top: 0;
  left: 100%;
  transform: translate(-100%);

  user-select: none;
  -webkit-tap-highlight-color: transparent;
  pointer-events: none;

  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  opacity: 0;
  z-index: 1;

  white-space: nowrap;

  ${({ $isPositive }) => css`
    color: ${$isPositive ? theme.BankrollDiff.colorPositive : theme.BankrollDiff.colorNegative};
  `}

  ${({ $isAnimating }) => {
    if ($isAnimating) {
      return css`
        animation:
          ${diffTranslationAnimation} ${DIFF_ANIM_DURATION}ms ease-out forwards,
          ${diffOpacityAnimation} ${DIFF_ANIM_DURATION}ms linear forwards;
      `;
    }
  }}

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 120%;
    height: 106%;
    background-color: ${theme.BankrollDiff.backgroundColor};
    z-index: -1;
    border-radius: 50em;
    transform: translate(-10%, -3%);
  }
`;

const AnimatedRealDiff = styled.div`
  ${sharedAnimatedDiffStyle}
`;

const AnimatedFreebetsDiff = styled.div`
  ${sharedAnimatedDiffStyle}
`;

const StyledAnimatedDigitsLabel = styled(AnimatedDigitsLabel)`
  margin-left: ${USER_INFO_AMOUNT_LEFT_MARGIN}em;
  max-width: calc(100% - ${USER_INFO_AMOUNT_ICON_WIDTH}em);

  ${({ $isYellow }) => css`
    color: ${$isYellow ? palette.yellowwinamax450 : palette.white};
  `};
`;

const useBankrollQueue = (realValue, freebetsValue) => {
  const prevRealValue = usePrevious(realValue);
  const prevFreebetsValue = usePrevious(freebetsValue);
  const prevRealValueRef = useRef(prevRealValue);
  const prevFreebetsValueRef = useRef(prevFreebetsValue);

  useLayoutEffect(() => {
    prevRealValueRef.current = prevRealValue;
  }, [prevRealValue]);
  useLayoutEffect(() => {
    prevFreebetsValueRef.current = prevFreebetsValue;
  }, [prevFreebetsValue]);

  const [realValuesQueue, setRealValuesQueue] = useState([]);
  const [freebetValuesQueue, setFreebetValuesQueue] = useState([]);

  useLayoutEffect(() => {
    setRealValuesQueue((prev) => [
      ...prev,
      {
        realValue,
        prevRealValue: prevRealValueRef.current,
      },
    ]);
  }, [realValue]);

  useLayoutEffect(() => {
    setFreebetValuesQueue((prev) => [
      ...prev,
      {
        freebetsValue,
        prevFreebetsValue: prevFreebetsValueRef.current,
      },
    ]);
  }, [freebetsValue]);

  const popRealQueue = useCallback(() => {
    if (realValuesQueue.length) {
      setRealValuesQueue((prev) => prev.slice(1));
    }
  }, [realValuesQueue]);

  const popFreebetsQueue = useCallback(() => {
    if (freebetValuesQueue.length) {
      setFreebetValuesQueue((prev) => prev.slice(1));
    }
  }, [freebetValuesQueue]);

  const result = useMemo(
    () => ({
      realValuesQueue,
      freebetValuesQueue,
      popRealQueue,
      popFreebetsQueue,
    }),
    [realValuesQueue, freebetValuesQueue, popRealQueue, popFreebetsQueue],
  );

  useLayoutEffect(() => {}, [result]);

  return result;
};
type UserBankrollProps = {
  bankrollFreebetValue: number;
  bankrollRealValue: number;
  bankrollVariation: UserBankrollEntityVariationState;
  className?: string;
};

const UserBankroll = ({
  bankrollRealValue,
  bankrollFreebetValue,
  bankrollVariation,
  className = '',
}: UserBankrollProps) => {
  const { balance: realVariation, freebets: freebetsVariation } = bankrollVariation ?? {};
  const { shouldAnimateBankroll: shouldAnimateRealVariation } = realVariation ?? {};
  const { shouldAnimateBankroll: shouldAnimateFreebetsVariation } = freebetsVariation ?? {};

  const soldeImgRef = useRef();
  const freebetsImgRef = useRef();

  const currency = getCurrency();
  const currencyRef = useRef();
  useLayoutEffect(() => {
    currencyRef.current = currency;
  }, [currency]);

  const { realValuesQueue, freebetValuesQueue, popRealQueue, popFreebetsQueue } = useBankrollQueue(
    bankrollRealValue,
    bankrollFreebetValue,
  );

  const popRealQueueRef = useRef();
  useLayoutEffect(() => {
    popRealQueueRef.current = popRealQueue;
  }, [popRealQueue]);
  const popFreebetsQueueRef = useRef();
  useLayoutEffect(() => {
    popFreebetsQueueRef.current = popFreebetsQueue;
  }, [popFreebetsQueue]);
  const currentRealGlobalTimeoutPid = useRef(null);
  const currentFreebetsGlobalTimeoutPid = useRef(null);
  const currentRealTimeoutPid = useRef(null);
  const currentFreebetsTimeoutPid = useRef(null);
  const currentRealPopDelayTimeoutPid = useRef(null);
  const currentFreebetsPopDelayTimeoutPid = useRef(null);

  const [realDiffValue, setRealDiffValue] = useState();
  const [freebetsDiffValue, setFreebetsDiffValue] = useState();

  const [displayedRealDiffValue, setDisplayedRealDiffValue] = useState(
    !NumberUtils.checkIsNaN(bankrollRealValue) ? Formatter.formatBankroll(bankrollRealValue, currency) : null,
  );
  const [displayedFreebetsDiffValue, setDisplayedFreebetsDiffValue] = useState(
    !NumberUtils.checkIsNaN(bankrollFreebetValue) ? Formatter.formatBankroll(bankrollFreebetValue, currency) : null,
  );

  const [realDiffIsAnimating, setIsRealDiffAnimating] = useState(false);
  const [freebetsDiffIsAnimating, setIsFreebetsDiffAnimating] = useState(false);

  const [realValuesCouple, setRealValuesCouple] = useState([]);
  const [freebetsValuesCouple, setFreebetsValuesCouple] = useState([]);

  useLayoutEffect(() => {
    /* Handle real value */

    if (
      realValuesQueue.length &&
      !currentRealTimeoutPid.current &&
      !currentRealPopDelayTimeoutPid.current &&
      !currentRealGlobalTimeoutPid.current
    ) {
      currentRealGlobalTimeoutPid.current = setTimeout(
        () => {
          const safePopRealQueue = popRealQueueRef.current;

          const getPopTimeoutCallback = (queue, popQueue) => () => {
            const [currRealBankroll] = queue;

            if (currRealBankroll) {
              const { realValue: rv, prevRealValue: prv } = currRealBankroll;

              if (prv) {
                setRealDiffValue(rv - prv);
                setIsRealDiffAnimating(true);
              }

              const getDiffTimeoutCallback = (prevVal, val) => () => {
                setRealValuesCouple([prevVal, val]);

                currentRealTimeoutPid.current = null;
                setRealDiffValue(null);
                setIsRealDiffAnimating(false);
                popQueue();
              };
              currentRealTimeoutPid.current = setTimeout(
                getDiffTimeoutCallback(prv, rv),
                shouldAnimateRealVariation ? DIFF_ANIM_DURATION : 0,
              );
            } else {
              currentRealTimeoutPid.current = null;
              setRealDiffValue(null);
              setIsRealDiffAnimating(false);
              popQueue();
            }

            currentRealPopDelayTimeoutPid.current = null;
          };

          currentRealPopDelayTimeoutPid.current = setTimeout(
            getPopTimeoutCallback(realValuesQueue, safePopRealQueue),
            shouldAnimateRealVariation ? TEXT_DISPLAY_TRANSITION_DELAY : 0,
          );
          currentRealGlobalTimeoutPid.current = null;
        },
        shouldAnimateRealVariation ? BANKROLL_CHANGES_START_ANIM_DELAY : 0,
      );
    }
  }, [realValuesQueue, shouldAnimateRealVariation]);

  useLayoutEffect(() => {
    const safeCurrency = currencyRef.current;

    setDisplayedRealDiffValue(() => {
      if (realDiffValue) {
        const formatted = Formatter.formatBankroll(realDiffValue, safeCurrency);

        return realDiffValue >= 0 ? `+${formatted}` : null;
      }

      return null;
    });
  }, [realDiffValue]);

  useLayoutEffect(() => {
    /* Handle freebets value */

    if (
      freebetValuesQueue.length &&
      !currentFreebetsTimeoutPid.current &&
      !currentFreebetsPopDelayTimeoutPid.current &&
      !currentFreebetsGlobalTimeoutPid.current
    ) {
      currentFreebetsGlobalTimeoutPid.current = setTimeout(
        () => {
          const safePopFreebetsQueue = popFreebetsQueueRef.current;

          const getPopTimeoutCallback = (queue, popQueue) => () => {
            const [currFreebetsBankroll] = queue;

            if (currFreebetsBankroll) {
              const { freebetsValue: fbv, prevFreebetsValue: pfbv } = currFreebetsBankroll;

              if (pfbv) {
                setFreebetsDiffValue(fbv - pfbv);
                setIsFreebetsDiffAnimating(true);
              }

              const getDiffTimeoutCallback = (prevVal, val) => () => {
                setFreebetsValuesCouple([prevVal, val]);

                currentFreebetsTimeoutPid.current = null;
                setFreebetsDiffValue(null);
                setIsFreebetsDiffAnimating(false);
                popQueue();
              };

              currentFreebetsTimeoutPid.current = setTimeout(
                getDiffTimeoutCallback(pfbv, fbv),
                shouldAnimateFreebetsVariation ? DIFF_ANIM_DURATION : 0,
              );
            } else {
              currentFreebetsTimeoutPid.current = null;
              setFreebetsDiffValue(null);
              setIsFreebetsDiffAnimating(false);
              popQueue();
            }

            currentFreebetsPopDelayTimeoutPid.current = null;
          };
          currentFreebetsPopDelayTimeoutPid.current = setTimeout(
            getPopTimeoutCallback(freebetValuesQueue, safePopFreebetsQueue),
            shouldAnimateFreebetsVariation ? TEXT_DISPLAY_TRANSITION_DELAY : 0,
          );
          currentFreebetsGlobalTimeoutPid.current = null;
        },
        shouldAnimateFreebetsVariation ? BANKROLL_CHANGES_START_ANIM_DELAY : 0,
      );
    }
  }, [freebetValuesQueue, shouldAnimateFreebetsVariation]);

  useLayoutEffect(() => {
    const safeCurrency = currencyRef.current;

    setDisplayedFreebetsDiffValue(() => {
      if (freebetsDiffValue) {
        const formatted = Formatter.formatBankroll(freebetsDiffValue, safeCurrency);

        return freebetsDiffValue >= 0 ? `+${formatted}` : null;
      }

      return null;
    });
  }, [freebetsDiffValue]);

  useLayoutEffect(() => {
    /* Clear timeouts on unmounting */
    return () => {
      if (currentRealPopDelayTimeoutPid.current) {
        clearTimeout(currentRealPopDelayTimeoutPid.current);
        currentRealPopDelayTimeoutPid.current = null;
      }
      if (currentFreebetsPopDelayTimeoutPid.current) {
        clearTimeout(currentFreebetsPopDelayTimeoutPid.current);
        currentFreebetsPopDelayTimeoutPid.current = null;
      }
      if (currentRealTimeoutPid.current) {
        clearTimeout(currentRealTimeoutPid.current);
        currentRealTimeoutPid.current = null;
      }
      if (currentFreebetsTimeoutPid.current) {
        clearTimeout(currentFreebetsTimeoutPid);
        currentFreebetsTimeoutPid.current = null;
      }
      if (currentRealGlobalTimeoutPid.current) {
        clearTimeout(currentRealGlobalTimeoutPid.current);
        currentRealGlobalTimeoutPid.current = null;
      }
      if (currentFreebetsGlobalTimeoutPid.current) {
        clearTimeout(currentFreebetsGlobalTimeoutPid.current);
        currentFreebetsGlobalTimeoutPid.current = null;
      }
    };
  }, []);

  const [prevRealValue, currentRealValue] = realValuesCouple;
  const [prevFreebetsValue, currentFreebetsValue] = freebetsValuesCouple;

  return (
    <Wrapper className={className}>
      {!Target.__LIBRARY__ || (Target.__LIBRARY__ && bankrollRealValue) ? (
        <RealValueAmoutLine>
          <SoldeImg id={'bankroll-real-icon'} ref={soldeImgRef} src={SoldeAsset} />
          <StyledAnimatedDigitsLabel $isYellow current={prevRealValue} next={currentRealValue ?? bankrollRealValue} />
          {shouldAnimateRealVariation ? (
            <AnimatedRealDiff $isPositive={realDiffValue >= 0} $isAnimating={realDiffIsAnimating}>
              {displayedRealDiffValue}
            </AnimatedRealDiff>
          ) : null}
        </RealValueAmoutLine>
      ) : null}

      {currentFreebetsValue || bankrollFreebetValue ? (
        <FreeBetValueAmountLine>
          <FreeBetsImg id={'bankroll-freebets-icon'} ref={freebetsImgRef} src={FreebetsIcon} />
          <StyledAnimatedDigitsLabel current={prevFreebetsValue} next={currentFreebetsValue || bankrollFreebetValue} />
          {shouldAnimateFreebetsVariation ? (
            <AnimatedFreebetsDiff $isPositive={freebetsDiffValue >= 0} $isAnimating={freebetsDiffIsAnimating}>
              {displayedFreebetsDiffValue}
            </AnimatedFreebetsDiff>
          ) : null}
        </FreeBetValueAmountLine>
      ) : null}
    </Wrapper>
  );
};

export default UserBankroll;
